import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Fingerprint, History } from 'lucide-react';
import { EmotionSection } from './EmotionSection';

const TraitBar = ({ trait, idx }) => (
  <div className="px-5 py-4" data-testid={`nexa-trait-${trait.id}`}>
    <div className="flex items-center justify-between text-[11px] font-mono tracking-widest uppercase mb-2">
      <span className={trait.value <= 50 ? 'text-sky-400' : 'text-white/40'}>{trait.left}</span>
      <span className={trait.value > 50 ? 'text-sky-400' : 'text-white/40'}>{trait.right}</span>
    </div>
    <div className="relative h-2 rounded-full bg-white/[0.07]">
      <div className="absolute left-1/2 top-0 h-full w-px bg-white/15" />
      <motion.div
        initial={{ left: '50%' }}
        animate={{ left: `${trait.value}%` }}
        transition={{ type: 'spring', stiffness: 120, damping: 20, delay: idx * 0.05 }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,0.6)]"
      />
    </div>
    {trait.note && <p className="text-[12px] text-white/45 mt-2.5 leading-relaxed">{trait.note}</p>}
  </div>
);

const ArchetypeRow = ({ a, idx }) => (
  <div className="px-5 py-4" data-testid={`nexa-archetype-${idx}`}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-sm text-white/90">{a.name}</span>
      <span className="text-[11px] font-mono text-sky-400">{a.strength}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${a.strength}%` }}
        transition={{ type: 'spring', stiffness: 100, damping: 22, delay: idx * 0.06 }}
        className="h-full rounded-full bg-sky-400/80"
      />
    </div>
    <p className="text-[12px] text-white/45 mt-2 leading-relaxed">{a.description}</p>
  </div>
);

const InsightCard = ({ insight, idx }) => {
  const [open, setOpen] = useState(idx < 2);
  return (
    <div className={idx > 0 ? 'border-t border-white/[0.06]' : ''}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nexa-insight-${insight.id}`}
        aria-expanded={open}
        className="w-full px-5 py-4 flex items-center justify-between text-left"
      >
        <span className="text-sm text-white/90">{insight.title}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} className="inline-flex text-white/40">
          <ChevronDown className="w-4 h-4" strokeWidth={2} />
        </motion.span>
      </button>
      {open && (
        <p className="px-5 pb-4 text-[13px] text-white/55 leading-relaxed -mt-1">{insight.content}</p>
      )}
    </div>
  );
};

const Section = ({ label, children }) => (
  <div>
    <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">{label}</h2>
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden divide-y divide-white/[0.06]">
      {children}
    </div>
  </div>
);

export const PersonalityDetail = ({ profile }) => {
  if (!profile || !profile.mbti) {
    return <p className="text-sm text-white/40">No personality profile yet — talk to NEXA about yourself.</p>;
  }
  const log = (profile.change_log || []).slice(0, 6);
  return (
    <div className="flex flex-col gap-8" data-testid="nexa-personality-detail">
      <div className="rounded-3xl border border-sky-400/20 bg-[#0A0A0A] p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl border border-sky-400/40 flex items-center justify-center">
            <Fingerprint className="w-5 h-5 text-sky-400" strokeWidth={1.6} />
          </div>
          <div>
            <p className="nexa-heading text-3xl font-light tracking-tight" data-testid="nexa-personality-mbti">
              {profile.mbti}
            </p>
            <p className="text-[11px] font-mono tracking-widest text-white/40 uppercase">
              earlier test · {profile.mbti_secondary}
            </p>
          </div>
        </div>
        <p className="text-sm text-sky-400/90 mt-4 font-medium">{profile.headline}</p>
        <p className="text-[13px] text-white/55 mt-2 leading-relaxed">{profile.summary}</p>
      </div>

      <EmotionSection />

      <Section label="Trait Spectrums">
        {(profile.traits || []).map((t, i) => <TraitBar key={t.id} trait={t} idx={i} />)}
      </Section>

      <Section label="Archetypes">
        {(profile.archetypes || [])
          .slice()
          .sort((a, b) => b.strength - a.strength)
          .map((a, i) => <ArchetypeRow key={a.name} a={a} idx={i} />)}
      </Section>

      <Section label="Insights">
        {(profile.insights || []).map((ins, i) => <InsightCard key={ins.id} insight={ins} idx={i} />)}
      </Section>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <History className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
          <span className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
            NEXA&apos;s updates
          </span>
        </div>
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden divide-y divide-white/[0.06]"
          data-testid="nexa-personality-log">
          {log.map((l, i) => (
            <div key={i} className="px-5 py-3.5">
              <p className="text-[10px] font-mono text-white/30 tracking-widest">
                {new Date(l.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <p className="text-[13px] text-white/70 mt-0.5">{l.change}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/30 mt-2 px-1">
          NEXA quietly refines this profile as you talk — every change lands here.
        </p>
      </div>
    </div>
  );
};
